const mapToOption = (value, options) => {
    if (!value) return "";
    const normalized = value.trim().toLowerCase();
    if (!normalized) return "";
    const exact = options.find(opt => opt === normalized);
    if (exact) return exact;
    return options.find(opt => normalized.includes(opt)) || "";
};

const CLOTHING_TYPES = ["t-shirt", "sweat", "pantalon", "robe", "veste", "chaussures", "accessoires", "manteau"];
const SEASONS = ["printemps", "été", "automne", "hiver"];
const OCCASIONS = ["casual", "travail", "sport", "soirée"];

const normalizeColor = (value) => {
    if (!value) return "";
    const normalized = value.trim().toLowerCase();
    const COLOR_MAP = {
        noir: ["noir", "black"],
        blanc: ["blanc", "white"],
        gris: ["gris", "gray", "grey"],
        bleu: ["bleu", "bleue", "blue", "navy"],
        rouge: ["rouge", "red", "bordeaux"],
        vert: ["vert", "verte", "green", "kaki", "olive"],
        jaune: ["jaune", "yellow", "or"],
        beige: ["beige", "sable", "sand", "camel"],
        rose: ["rose", "pink", "fuchsia"],
        marron: ["marron", "brown", "chocolat", "camel"],
        violet: ["violet", "violette", "purple", "lila", "lavande"],
        orange: ["orange"],
    };

    for (const [key, aliases] of Object.entries(COLOR_MAP)) {
        if (normalized === key) return key;
        if (aliases.some(alias => normalized.includes(alias))) {
            return key;
        }
    }

    return normalized.split(" ")[0];
};

export async function analyzeClothingImage(dataUrl) {
    if (!dataUrl?.startsWith("data:image")) {
        throw new Error("Image invalide.");
    }

    // Always call relative path; CRA proxies in dev via package.json "proxy"
    const apiUrl = '/api/analyze-clothing';

    const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ dataUrl }),
    });

    if (!response.ok) {
        let errorText = '';
        try {
            errorText = await response.text();
        } catch {}
        const err = new Error(errorText || "Erreur lors de l'analyse du vêtement.");
        // Expose HTTP status and retry-after header
        err.status = response.status;
        const retryAfter = response.headers.get('Retry-After') || response.headers.get('retry-after');
        if (retryAfter) {
            const secs = parseFloat(retryAfter);
            if (!Number.isNaN(secs) && secs > 0) {
                err.retryAfterMs = Math.ceil(secs * 1000);
            }
        }
        throw err;
    }

    const data = await response.json();
    const rawContent =
        data.choices?.[0]?.message?.content ??
        (Array.isArray(data.output) ? data.output[0]?.content?.[0]?.text : null);

    if (!rawContent) {
        throw new Error("Réponse vide de l'IA.");
    }

    let parsed;
    try {
        parsed = typeof rawContent === "string" ? JSON.parse(rawContent) : JSON.parse(rawContent[0]?.text || "{}");
    } catch (err) {
        console.error("Erreur parsing analyse IA :", err, rawContent);
        throw new Error("Impossible d'interpréter la réponse de l'IA.");
    }

    const normalizedType = mapToOption(parsed.type, CLOTHING_TYPES);
    const normalizedSeason = mapToOption(parsed.season, SEASONS);
    const normalizedOccasions = Array.isArray(parsed.occasions)
        ? parsed.occasions
              .map(occ => mapToOption(occ, OCCASIONS))
              .filter(Boolean)
              .slice(0, 3)
        : [];

    // Support both single color (old) and multiple colors (new)
    let normalizedColors = [];
    if (Array.isArray(parsed.colors)) {
        normalizedColors = parsed.colors
            .map(c => normalizeColor(c))
            .filter(Boolean);
    } else if (parsed.color) {
        // Fallback to old single color field
        const singleColor = normalizeColor(parsed.color);
        if (singleColor) normalizedColors = [singleColor];
    }

    return {
        type: normalizedType,
        colors: normalizedColors,
        season: normalizedSeason,
        occasions: normalizedOccasions,
        title: (parsed.title || parsed.name || "").trim(),
        details: parsed.details || "",
    };
}
